import { type ReactNode } from 'react';
import { ProductImage, type ProductImageProps } from './ProductImage.js';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import { MarketingHeader, MarketingFooter, type MarketingLandingProps } from './MarketingHome.js';
import pageData from './marketing-pages.json';
import './marketing-detail.css';

export interface MarketingBlock {
  heading: string;
  paragraphs: string[];
  kind?: string;
  bullets?: string[];
  table?: string[][];
}
export interface MarketingPage {
  slug: string;
  target: string;
  title: string;
  description: string;
  heading: string;
  headline?: string;
  lead: string;
  intro?: string[];
  bullets?: string[];
  image?: ProductImageProps;
  closingHeading?: string;
  closingText?: string;
  cta?: string;
  asset: string;
  alt: string;
  blocks: MarketingBlock[];
}
export const marketingPages: MarketingPage[] = pageData;
export function marketingPageAt(pathname: string): MarketingPage | undefined {
  const path = pathname.replace(/\/+$/, '').replace(/^\//, '');
  return marketingPages.find(page => page.slug === path);
}
// Trusted, checked-in copy is rendered as React text and explicit links. No HTML
// injection or remote Markdown renderer sits on the public entry/auth boundary.
function Inline({ text }: { text: string }) {
  const pieces: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*([^*]+)\*|==([^=]+)==/g;
  let offset = 0;
  for (const match of text.matchAll(re)) {
    pieces.push(text.slice(offset, match.index));
    if (match[1] && match[2]) pieces.push(<a key={match.index} href={match[2]}>{match[1]}</a>);
    else if (match[4]) pieces.push(<mark className="bol-key-detail" key={match.index}>{match[4]}</mark>);
    else pieces.push(<em key={match.index}>{match[3]}</em>);
    offset = (match.index ?? 0) + match[0].length;
  }
  pieces.push(text.slice(offset));
  return <>{pieces}</>;
}
function Paragraphs({ block }: { block: MarketingBlock }) {
  return <>
    {block.paragraphs.map((paragraph, index) => <p key={index}><Inline text={paragraph} /></p>)}
    {block.bullets ? <ul>{block.bullets.map(item => <li key={item}><Inline text={item} /></li>)}</ul> : null}
    {block.table?.length ? <div className="bol-detail__comparison">
      {block.table.slice(1).map((row, i) => <section key={i} className="bol-detail__comparison-row">
        <h3>{row[0]}</h3><dl>{row.slice(1).map((cell, j) => <div key={j}><dt>{block.table?.[0]?.[j + 1]}</dt><dd><Inline text={cell} /></dd></div>)}</dl>
      </section>)}
    </div> : null}
  </>;
}
export function MarketingDetail({ page, onGetStarted, onSignIn }: MarketingLandingProps & { page: MarketingPage }) {
  const questions = page.blocks.filter(block => block.kind === 'question');
  const sections = page.blocks.filter(block => block.kind !== 'question');
  return <div className="bol-landing bol-detail-page">
    <a className="bol-detail__skip" href="#landing-main">Skip to content</a>
    <MarketingHeader onGetStarted={onGetStarted} onSignIn={onSignIn} />
    <main id="landing-main">
      <header className="bol-detail__hero">
        <a className="bol-detail__breadcrumb" href="/ai-assistant-for-business/#landing-top">Business Ops <ArrowRight size={16} aria-hidden="true" /></a>
        <div className={`bol-detail__opening${page.image ? ' bol-detail__opening--with-image' : ''}`}>
        <h1>{page.headline ? <><span className="bol-detail__category">{page.target}</span>{page.headline}</> : page.heading}</h1>
        {page.image ? <ProductImage {...page.image} priority /> : null}
        <div className="bol-detail__opening-copy">
        <p className="bol-detail__lead"><Inline text={page.lead} /></p>
        {page.intro?.map(text => <p className="bol-detail__intro" key={text}><Inline text={text} /></p>)}
        <button className="bol-detail__button" type="button" onClick={onGetStarted}>{page.cta ?? 'Create your account'} <ArrowUpRight size={20} aria-hidden="true" /></button>
        <p className="bol-detail__offer">Three-day trial · Prices to be announced</p>
        {page.bullets ? <ul className="bol-detail__benefits">{page.bullets.map(text => <li key={text}>{text}</li>)}</ul> : null}
        </div></div>
      </header>
      <div className="bol-detail__body">
        {sections.map(block => <section key={block.heading} className="bol-detail__section"><h2>{block.heading}</h2><div className="bol-detail__prose"><Paragraphs block={block} /></div></section>)}
        {questions.length ? <section className="bol-detail__questions"><h2>Before you start</h2><div>{questions.map(block => <details key={block.heading}><summary>{block.heading}<ChevronDown size={22} aria-hidden="true" /></summary><Paragraphs block={block} /></details>)}</div></section> : null}
      </div>
      <section className="bol-detail__closing"><div><p className="bol-detail__label">BUSINESS OPS LOOP</p><h2>{page.closingHeading ?? 'Start with work you actually need done.'}</h2><p>{page.closingText ?? 'Bring your question and the work you need prepared.'}</p></div><div className="bol-detail__closing-action"><button className="bol-detail__button" type="button" onClick={onGetStarted}>{page.cta ?? 'Create your account'} <ArrowUpRight size={20} aria-hidden="true" /></button><a href="/ai-assistant-for-business/#landing-pricing">See the three plans</a></div></section>
      <aside className="bol-detail__availability"><strong>Currently in testing.</strong> These pages show what we’re building. Setup will show which features and connections you can use.</aside>
    </main>
    <MarketingFooter onGetStarted={onGetStarted} onSignIn={onSignIn} />
  </div>;
}
